import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Queries the backend for the user's current session expiry.
 * Automatically updates localStorage and emits lifecycle events.
 *
 * @returns {Promise<boolean>} true if sync succeeded, false otherwise
 */
export const syncSessionExpiry = async () => {
    try {
        // Simple heartbeat to prevent Chrome caching
        const timestamp = Date.now();

        // Abort if no login flag present
        const loggedIn = localStorage.getItem("userLoggedIn") === "true";

        if(!loggedIn) return false;

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        const response = await axios.get(
            `${API_URL}/api/auth/session-expiry?_=${timestamp}`,
            { signal: controller.signal, withCredentials: true },
        );

        clearTimeout(timeout);

        if(!response?.data?.session_expiry) return false;

        const dbExpiry = new Date(response.data.session_expiry);
        const now = new Date();

        localStorage.setItem("last_session_sync", now.getTime().toString());
        localStorage.setItem("session_expiry", dbExpiry.toISOString());

        // If expired according to server → fire event + logout logic elsewhere
        if(dbExpiry <= now) {
            localStorage.removeItem("last_session_sync");
            localStorage.removeItem("session_expiry");

            window.dispatchEvent(
                new CustomEvent("session-expired", {
                    detail: {
                        expiry: dbExpiry.toISOString(),
                        reason: "server-expired",
                    },
                }),
            );

            return false;
        }

        // Dispatch an event for UI components that listen for session updates
        window.dispatchEvent(
            new CustomEvent("session-synced", {
                detail: {
                    expiry: dbExpiry.toISOString(),
                    reason: "synced",
                },
            }),
        );

        return true;
    } catch(err) {
        console.error("Session sync failed:", err?.message || err);

        // 401 means the session is definitely gone
        if(err?.response?.status === 401) {
            window.dispatchEvent(
                new CustomEvent("session-expired", {
                    detail: { reason: "401" },
                }),
            );
        }

        return false;
    }
};

/**
 * Starts a repeating timer to keep session expiry in sync.
 * @param {number} intervalMinutes - How often to sync, default 5 minutes.
 * @returns {Function} cleanup function to stop syncing.
 */
export const setupSessionExpirySync = () => {
    const interval = 5 * 60 * 1000;

    // Immediate sync
    syncSessionExpiry();

    // Periodic sync
    const id = setInterval(syncSessionExpiry, interval);

    // Cleanup handler
    return () => clearInterval(id);
};
